import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import StatusCard from "../../components/StatusCard/StatusCard";

const PayrollSummary = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Months data with emojis
  const months = [
    { number: 1, name: "January", emoji: "❄️" },
    { number: 2, name: "February", emoji: "💝" },
    { number: 3, name: "March", emoji: "🌸" },
    { number: 4, name: "April", emoji: "🌷" },
    { number: 5, name: "May", emoji: "🌺" },
    { number: 6, name: "June", emoji: "☀️" },
    { number: 7, name: "July", emoji: "🌞" },
    { number: 8, name: "August", emoji: "🌻" },
    { number: 9, name: "September", emoji: "🍂" },
    { number: 10, name: "October", emoji: "🎃" },
    { number: 11, name: "November", emoji: "🦃" },
    { number: 12, name: "December", emoji: "🎄" },
  ];

  // Keep the existing useEffect
  useEffect(() => {
    fetchPayrollSummaries();
  }, []);

  // Add this new useEffect for default selections
  useEffect(() => {
    if (payrolls.length > 0 && !selectedPayroll) {
      // Select first payroll by default
      const firstPayroll = payrolls[0];
      setSelectedPayroll(firstPayroll);

      // Select current month by default
      const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11
      const currentMonthObj = months.find(
        (month) => month.number === currentMonth
      );
      if (currentMonthObj) {
        setSelectedMonth(currentMonthObj);
        // Automatically fetch data for default selections
        fetchSalaryReport(
          firstPayroll.payrollId,
          currentMonthObj.number,
          selectedYear
        );
      }
    }
  }, [payrolls, selectedYear]); // Run when payrolls data is loaded

  useEffect(() => {
    if (selectedPayroll && selectedMonth) {
      fetchSalaryReport(
        selectedPayroll.payrollId,
        selectedMonth.number,
        selectedYear
      );
    }
  }, [selectedYear]);

  const fetchPayrollSummaries = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/summaries`
      );
      const result = response.data;

      if (result.status === 200) {
        setPayrolls(result.data);
      } else {
        setError(result.message || "Failed to fetch payroll summaries");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching payroll summaries");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalaryReport = async (payrollId, month, yearParam) => {
    setLoading(true);
    setIsSubmitting(true);
    setSalaryData([]);

    const year = yearParam || selectedYear;
    const payrollDate = `${year}-${String(month).padStart(2, "0")}-01`;

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/report/payroll-summary`,
        {
          payrollId: payrollId,
          payrollDate: payrollDate,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const result = response.data;
      if (result.status === 200) {
        setSalaryData(result.data);
      } else {
        setError(result.message || "Failed to fetch salary report");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching salary report");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  // This function is already properly implemented - no changes needed
  const handlePayrollSelection = (payroll) => {
    setSelectedPayroll(payroll);
    setError(null);
    if (selectedMonth) {
      fetchSalaryReport(payroll.payrollId, selectedMonth.number, selectedYear);
    }
  };

  const handleMonthSelection = (month) => {
    setSelectedMonth(month);
    setError(null);
    if (selectedPayroll) {
      fetchSalaryReport(selectedPayroll.payrollId, month.number, selectedYear);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateTotals = () => {
    return salaryData.reduce(
      (acc, employee) => {
        acc.totalGross += employee.earnedGrossSalary || 0;
        acc.totalNetPay += employee.earnedNetPay || 0;
        acc.totalPaid += employee.paidAmount || 0;
        acc.totalCTC += employee.ctc || 0;
        acc.totalOtherAllowances += employee.earnedOtherAllowances || 0;
        return acc;
      },
      {
        totalGross: 0,
        totalNetPay: 0,
        totalPaid: 0,
        totalCTC: 0,
        totalOtherAllowances: 0,
      }
    );
  };

  const totals = salaryData.length > 0 ? calculateTotals() : null;

  return (
    <div className="salary-registry">
      {/* New Payroll Selection Section */}
      <div className="payroll-selection">
        <div className="payroll-selection-header">
          <h2 className="heading6">Payroll Summary</h2>

          <div className="year-selector">
            <div className="year-arrow-controls">
              <button
                className="year-arrow left"
                aria-label="Previous year"
                type="button"
                disabled={selectedYear <= 1900}
                onClick={() => setSelectedYear((y) => Math.max(1900, y - 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M15.7 5.7a1 1 0 0 1 0 1.4L10.42 12l5.3 4.9a1 1 0 0 1-1.42 1.4l-6-5.5a1 1 0 0 1 0-1.4l6-5.5a1 1 0 0 1 1.4 0z" />
                </svg>
              </button>
              <input
                id="year-input"
                type="number"
                min={1900}
                max={2100}
                className="year-value"
                value={selectedYear}
                onChange={(e) => {
                  let val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 9999)
                    setSelectedYear(val);
                }}
                placeholder="YYYY"
                style={{ width: 70, textAlign: "center" }}
              />
              <button
                className="year-arrow right"
                aria-label="Next year"
                type="button"
                disabled={selectedYear >= 2100}
                onClick={() => setSelectedYear((y) => Math.min(2100, y + 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M8.3 18.3a1 1 0 0 1 0-1.4L13.58 12 8.3 7.1a1 1 0 0 1 1.42-1.4l6 5.5a1 1 0 0 1 0 1.4l-6 5.5a1 1 0 0 1-1.4 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="payroll-buttons-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="loading-skeleton"
                style={{ height: "64px" }}
              ></div>
            ))}
          </div>
        ) : (
          <div className="payroll-buttons-grid">
            {payrolls.length > 0 ? (
              payrolls.map((payroll) => (
                <button
                  key={payroll.payrollId}
                  onClick={() => handlePayrollSelection(payroll)}
                  className={`payroll-button ${
                    selectedPayroll?.payrollId === payroll.payrollId
                      ? "active"
                      : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="payroll-button-content">
                    <div className="payroll-text">
                      <div className="payroll-name">
                        {payroll.payrollName || `Payroll ${payroll.payrollId}`}
                      </div>
                      <div
                        className={`payroll-type-badge ${
                          payroll.payrollSetupType?.toLowerCase() === "auto"
                            ? "auto"
                            : "manual"
                        }`}
                      >
                        <span className="badge-icon">⚙️</span>
                        {payroll.payrollSetupType}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="error">
                No payroll configurations found. Please create a payroll
                configuration first.
              </div>
            )}
          </div>
        )}

        {/* Month Selection Section */}
        {selectedPayroll && (
          <div className="month-selection">
            <div className="month-cards-grid">
              {months.map((month) => (
                <button
                  key={month.number}
                  onClick={() => handleMonthSelection(month)}
                  className={`month-card ${
                    selectedMonth?.number === month.number ? "active" : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="month-emoji">{month.emoji}</div>
                  <div className="month-name">{month.name}</div>
                  <div className="month-year">{selectedYear}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="salary-registry__error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading && <LoadingScreen />}

      {totals && (
        <div className="salary-registry__summary">
          <div className="stats-grid">
            {/* Changed from 'earned-gross' to 'total-gross' to match SCSS */}
            <div className="stat-card total-gross">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalGross)}</h3>
                <p>Total Earned Gross Salary</p>
              </div>
            </div>
            {/* Changed from 'earned-net' to 'total-net' to match SCSS */}
            <div className="stat-card total-net">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💵</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalNetPay)}</h3>
                <p>Total Earned Net Pay</p>
              </div>
            </div>
            {/* Changed from 'other-allowances' to a new class that will be styled */}
            <div className="stat-card other-allowances">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">🎁</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalOtherAllowances)}</h3>
                <p>Total Other Allowances</p>
              </div>
            </div>
            {/* Changed from 'total-paid' to a new class that will be styled */}
            <div className="stat-card total-paid">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💳</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalPaid)}</h3>
                <p>Total Paid Amount</p>
              </div>
            </div>
            {/* Changed from 'total-ctc' to match the existing SCSS class */}
            <div className="stat-card total-ctc">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalCTC)}</h3>
                <p>Total CTC</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {salaryData.length > 0 && (
        <>
          <div className="salary-registry__table-container">
            <table className="salary-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="employee-header">
                    Employee
                  </th>
                  <th rowSpan="2">Employee ID</th>
                  <th className="date-header" rowSpan="2">
                    Payroll Date
                  </th>
                  <th className="date-header" rowSpan="2">
                    Payment Date
                  </th>
                  <th colSpan="5" className="earnings-header">
                    Salary Components
                  </th>
                  <th rowSpan="2" className="payment-header">
                    Paid Amount
                  </th>
                  <th rowSpan="2" className="ctc-header">
                    CTC
                  </th>
                  <th colSpan="2" className="status-header">
                    Status
                  </th>
                </tr>

                {/* Sub Header Row - Reordered to place Earned Other Allowances before Earned Net */}
                <tr>
                  <th className="earnings-header">Fixed Gross</th>
                  <th className="earnings-header">Fixed Net</th>
                  <th className="earnings-header">Earned Gross</th>
                  <th className="earnings-header">Earned Other Allowances</th>
                  <th className="earnings-header">Earned Net</th>
                  <th className="status-header">Approval Status</th>
                  <th className="status-header">Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {salaryData.map((record, index) => (
                  <tr key={index}>
                    <td className="employee-column">{record.employeeName}</td>
                    <td className="employee-id-column">
                      {record.userEmployeeId || "N/A"}
                    </td>
                    <td className="date-column">
                      {formatDate(record.payrollDate)}
                    </td>
                    <td className="date-column">
                      {formatDate(record.paymentDate)}
                    </td>
                    <td className="earnings-column">
                      {formatCurrency(record.fixedGrossSalary)}
                    </td>
                    <td className="earnings-column">
                      {formatCurrency(record.fixedNetPay)}
                    </td>
                    <td className="earnings-column">
                      {formatCurrency(record.earnedGrossSalary)}
                    </td>
                    <td className="earnings-column">
                      {formatCurrency(record.earnedOtherAllowances || 0)}
                    </td>
                    <td className="earnings-column">
                      {formatCurrency(record.earnedNetPay)}
                    </td>
                    <td className="payment-column">
                      {formatCurrency(record.paidAmount)}
                    </td>
                    <td className="ctc-column">{formatCurrency(record.ctc)}</td>
                    <td className="status-column">
                      <StatusCard status={record.approvalStatus} />
                    </td>
                    <td className="status-column">
                      <StatusCard status={record.paymentStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading &&
        !error &&
        salaryData.length === 0 &&
        selectedPayroll &&
        selectedMonth && (
          <div className="salary-registry__empty">
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h3>No Data Available</h3>
              <p>No salary data found for the selected payroll and month.</p>
            </div>
          </div>
        )}
    </div>
  );
};

export default PayrollSummary;
